import { Stack } from "@mui/material";
import { Alert } from "Components/_Shared/Alert";
import type { AlertVariant } from "Components/_Shared/Alert";
import { Button } from "Components/_Shared/Buttons/Button";
import type { ClientDetailResponseClient } from "Infrastructure/ApiZfp/Schema";
import { NsureClientCaseStateCaseStatus } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import type { FC } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGoalsFulfillmentContext } from "./GoalsFulfillmentContext";
import { useGoalFulfillmentAlerts } from "./useGoalFulfillmentAlerts";

type Props = {
  client?: ClientDetailResponseClient;
};

export const GoalFulfillmentAlerts: FC<Props> = ({ client }) => {
  const result = useGoalFulfillmentAlerts({ client });
  const { t } = useResource();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAnyEvaluationWarning, isInvestmentCaseLocked, lockedCaseStatus, growUpContractingLink } = useGoalsFulfillmentContext();

  const alerts = result?.alerts ?? [];

  const alertsWithEvaluation = hasAnyEvaluationWarning
    ? [
        ...alerts,
        {
          key: "evaluation-warning",
          text: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.WarningAlert),
        },
      ]
    : alerts;

  const caseStatusVariantMap: Partial<Record<Exclude<NsureClientCaseStateCaseStatus, null>, AlertVariant>> = {
    [NsureClientCaseStateCaseStatus.InSignature]: "info",
    [NsureClientCaseStateCaseStatus.Signed]: "success",
    [NsureClientCaseStateCaseStatus.Done]: "success",
    [NsureClientCaseStateCaseStatus.Rejected]: "warning",
    [NsureClientCaseStateCaseStatus.Cancelled]: "warning",
    [NsureClientCaseStateCaseStatus.Error]: "warning",
  };

  const caseStatusTitleMap: Partial<Record<Exclude<NsureClientCaseStateCaseStatus, null>, string>> = {
    [NsureClientCaseStateCaseStatus.InSignature]: t(Resources.Questionnaire.InvestmentCaseStatus.StatusTitles.InSignature),
    [NsureClientCaseStateCaseStatus.Signed]: t(Resources.Questionnaire.InvestmentCaseStatus.StatusTitles.Signed),
    [NsureClientCaseStateCaseStatus.Done]: t(Resources.Questionnaire.InvestmentCaseStatus.StatusTitles.Done),
    [NsureClientCaseStateCaseStatus.Rejected]: t(Resources.Questionnaire.InvestmentCaseStatus.StatusTitles.Rejected),
    [NsureClientCaseStateCaseStatus.Cancelled]: t(Resources.Questionnaire.InvestmentCaseStatus.StatusTitles.Cancelled),
    [NsureClientCaseStateCaseStatus.Error]: t(Resources.Questionnaire.InvestmentCaseStatus.StatusTitles.Error),
  };

  const allAlerts = isInvestmentCaseLocked
    ? [
        ...alertsWithEvaluation,
        {
          key: "investment-case-status",
          variant: (lockedCaseStatus && caseStatusVariantMap[lockedCaseStatus]) ?? ("warning" as AlertVariant),
          title: (lockedCaseStatus && caseStatusTitleMap[lockedCaseStatus]) ?? t(Resources.Questionnaire.InvestmentCaseStatus.Title),
          text: t(Resources.Questionnaire.InvestmentCaseStatus.Text),
          ctaHref: growUpContractingLink?.url,
        },
      ]
    : alertsWithEvaluation;

  if (allAlerts.length === 0) return null;

  return (
    <Stack spacing={Spacing.M} pr={Spacing.XXL}>
      {allAlerts.map(({ key, text, title, variant = "warning", ctaTab, ctaHref }) => (
        <Alert
          key={key}
          variant={variant}
          title={title}
          text={text}
          action={
            ctaTab ? (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={() => {
                  const params = new URLSearchParams(location.search);
                  params.set("tab", ctaTab);
                  navigate(`${location.pathname}?${params.toString()}`);
                }}
              >
                {t(Resources.Common.Resolve)}
              </Button>
            ) : ctaHref ? (
              <Button
                size="small"
                variant="outlined"
                color={variant === "gray" || variant === "success" ? "inherit" : variant}
                sx={
                  variant === "success"
                    ? { color: "success.dark", borderColor: "success.dark", "&:hover": { borderColor: "success.dark" } }
                    : undefined
                }
                onClick={() => window.open(ctaHref, "_blank", "noopener,noreferrer")}
              >
                {t(Resources.Common.Detail)}
              </Button>
            ) : undefined
          }
        />
      ))}
    </Stack>
  );
};
